import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Heading, Image, SimpleGrid, Text, VStack } from '@chakra-ui/react';

const LOGO_CDN_BASE = 'https://img.logo.dev';
const LOGO_CDN_TOKEN = 'pk_D_GV9HLPT_mEGmOXI16SGA';

const normalizeCompanies = (companies) => {
  if (!Array.isArray(companies)) {
    return [];
  }

  return companies
    .map((company, index) => {
      const website = String(company?.website || '').trim();
      const name = String(company?.name || company?.client || '').trim();
      const description = String(company?.description || '').trim();

      if (!website || !name) {
        return null;
      }

      return {
        id: `${website}-${index}`,
        website,
        name,
        description
      };
    })
    .filter(Boolean);
};

const CompaniesShowcase = ({
  companies,
  heading = 'Companies',
  maxVisible = 5,
  imageHeight = '90px',
  containerProps = {},
  headingProps = {},
  gridProps = {},
  emptyFallback = null,
  showDescriptions = false,
  // Optional: render the heading yourself (e.g. the public page's SectionTitle).
  renderHeading
}) => {
  const normalizedCompanies = useMemo(() => normalizeCompanies(companies), [companies]);
  const [hiddenWebsites, setHiddenWebsites] = useState([]);

  useEffect(() => {
    setHiddenWebsites([]);
  }, [normalizedCompanies]);

  const handleLogoError = useCallback((website) => {
    if (!website) {
      return;
    }
    setHiddenWebsites((prev) => (prev.includes(website) ? prev : [...prev, website]));
  }, []);

  const visibleCompanies = useMemo(() => {
    return normalizedCompanies
      .filter((company) => !hiddenWebsites.includes(company.website))
      .slice(0, maxVisible);
  }, [hiddenWebsites, maxVisible, normalizedCompanies]);

  const autoFitTemplate = 'repeat(auto-fit, minmax(110px, 1fr))';
  const fixedColumnTemplate = `repeat(${Math.max(visibleCompanies.length || 1, 1)}, minmax(0, 1fr))`;

  if (visibleCompanies.length === 0) {
    return emptyFallback;
  }

  return (
    <Box bg="white" boxShadow="md" borderRadius="md" p={6} {...containerProps}>
      {heading && renderHeading ? renderHeading(heading) : null}
      {heading && !renderHeading ? (
        <Heading as="h2" size="md" mb={4} {...headingProps}>
          {heading}
        </Heading>
      ) : null}
      <SimpleGrid
        w="full"
        spacing={{ base: 4, md: 6 }}
        justifyItems="center"
        alignItems="center"
        templateColumns={visibleCompanies.length <= 3 ? fixedColumnTemplate : autoFitTemplate}
        {...gridProps}
      >
        {visibleCompanies.map((company) => {
          const logoUrl = `${LOGO_CDN_BASE}/${company.website}?token=${LOGO_CDN_TOKEN}&format=png&size=90&fallback=404`;
          return (
            <VStack key={company.id} spacing={2} align="center" w="full" textAlign="center">
              <Image
                src={logoUrl}
                alt={company.name}
                h={imageHeight}
                objectFit="contain"
                onError={() => handleLogoError(company.website)}
              />
              <Box>
                <Text fontWeight="semibold" color="gray.800">
                  {company.name}
                </Text>
                {showDescriptions && company.description ? (
                  <Text color="gray.600" fontSize="sm">
                    {company.description}
                  </Text>
                ) : null}
              </Box>
            </VStack>
          );
        })}
      </SimpleGrid>
    </Box>
  );
};

export default CompaniesShowcase;
